/**
 * Sandboxed banner-ad renderer. Two render modes:
 *   1. htmlSandboxed != null: render via <iframe srcdoc> with
 *      sandbox="allow-scripts" only. Explicitly NO allow-popups,
 *      NO allow-top-navigation, NO allow-same-origin so a misbehaving
 *      creative can't break out of the iframe or redirect the parent.
 *   2. htmlSandboxed == null: render a plain <a><img> with the click
 *      proxied through /api/ads/click/:creativeId for tracking.
 *
 * Mobile awareness is the parent slot's responsibility (the SIDEBAR
 * wrapper hides itself below 768px, etc.) so this component just
 * renders into whatever slot it gets.
 */
import type { AdBannerDto, AdBannerSlot } from '../api/ads';
import { useAdsBanners } from '../api/ads';
import { usePublicConfig } from '../api/public-config';

interface Props {
  slot: AdBannerSlot;
  className?: string;
  // Inline style overrides; mostly for parent slots that need to size
  // the iframe (banners are content-sized otherwise).
  height?: number;
}

export function AdBanner({ slot, className, height }: Props): React.ReactElement | null {
  const adsGloballyEnabled = usePublicConfig((s) => s.config.adsGloballyEnabled);
  const { data } = useAdsBanners(slot, adsGloballyEnabled);
  const banners = data?.banners ?? [];
  if (banners.length === 0) return null;

  return (
    <div className={`sl-ad-banner-slot sl-ad-banner-${slot.toLowerCase()} ${className ?? ''}`}>
      {banners.map((b) => (
        <BannerCreative key={b.creativeId} banner={b} height={height} />
      ))}
    </div>
  );
}

function BannerCreative({ banner, height }: { banner: AdBannerDto; height?: number }): React.ReactElement {
  if (banner.htmlSandboxed) {
    return (
      <iframe
        title="Sponsored"
        sandbox="allow-scripts"
        srcDoc={banner.htmlSandboxed}
        style={{
          width: '100%',
          height: height ?? 90,
          border: 'none',
          display: 'block',
          background: 'transparent',
        }}
      />
    );
  }
  if (banner.iconUrl && banner.clickUrl) {
    return (
      <a
        href={`/api/ads/click/${banner.creativeId}`}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="sl-ad-banner-image"
        title="Sponsored"
      >
        <img src={banner.iconUrl} alt="Sponsored" style={{ maxWidth: '100%', display: 'block' }} />
      </a>
    );
  }
  return <span />;
}
